import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { useAuthToken } from '@tribeplatform/react-sdk/hooks'

import { Appearance, AppInitialProps, ColorMode } from '../@types'
import { setAppearance as setAppearanceCookie } from '../server/utils/appearance.utils'
import { BASE_THEME } from './base.theme'
import { ThemeConvertor } from './theme-convertor'
import { Theme } from './types'
import { applyDarkMode, applyTheme } from './utils'

const DEFAULT_APPEARANCE: Appearance = {
  colorMode: 'light',
  syncColorMode: false,
}

export type ThemeProps = {
  theme: Theme
  darkMode: boolean
  appearance: Appearance
  setDarkMode: Dispatch<SetStateAction<boolean>>
  toggleDarkMode: () => void
  setAppearance: (appearance: Appearance) => void
}

export const ThemeContext = createContext<ThemeProps>({
  theme: BASE_THEME,
  darkMode: false,
  appearance: DEFAULT_APPEARANCE,
  setDarkMode: () => undefined,
  toggleDarkMode: () => undefined,
  setAppearance: () => undefined,
})

export const useTheme = (): ThemeProps => {
  const context = useContext(ThemeContext)
  return context
}

export type ThemeProviderProps = {
  customTheme?: Theme
  customDarkMode?: boolean
  appearance?: AppInitialProps['appearance']
}

export const ThemeProvider: FC<ThemeProviderProps> = ({
  children,
  customTheme,
  appearance,
}) => {
  const userDefinedSettings = !!appearance
  const [appearanceState, setAppearanceState] = useState<Appearance>(
    appearance || DEFAULT_APPEARANCE,
  )
  const {
    data: { network },
  } = useAuthToken()

  const { syncColorMode, colorMode } = appearanceState

  const setAppearance = useCallback((appearance: Appearance) => {
    setAppearanceCookie(appearance)
    setAppearanceState(appearance)
  }, [])

  const setMode = useCallback(
    (colorMode: ColorMode) => {
      const app: Appearance = {
        ...appearanceState,
        colorMode,
      }
      setAppearance(app)
    },
    [appearanceState, setAppearance],
  )

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = event => {
      if (userDefinedSettings && syncColorMode) {
        setMode(event.matches ? 'dark' : 'light')
      }
    }

    if (mediaQuery?.addEventListener) {
      mediaQuery.addEventListener('change', handler)
    } else {
      mediaQuery.addListener(handler)
    }

    return () => {
      if (mediaQuery?.removeEventListener) {
        mediaQuery.removeEventListener('change', handler)
      } else {
        mediaQuery.removeListener(handler)
      }
    }
  }, [appearanceState, userDefinedSettings])

  const theme =
    customTheme ||
    new ThemeConvertor({
      base: BASE_THEME,
      networkOldTheme: network?.themes?.active,
      networkTheme: network?.activeTheme,
    }).toTheme()

  useEffect(() => {
    applyTheme(document, theme, colorMode === 'dark')
  }, [JSON.stringify(theme), colorMode === 'dark'])

  useEffect(() => {
    applyDarkMode(document.documentElement, colorMode === 'dark')
  }, [colorMode])

  return (
    <ThemeContext.Provider
      value={{
        theme,
        darkMode: colorMode === 'dark',
        appearance: appearanceState,
        setDarkMode: isDark => {
          setMode(isDark ? 'dark' : 'light')
        },
        toggleDarkMode: () => {
          setMode(colorMode === 'dark' ? 'light' : 'dark')
        },
        setAppearance,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}
